<template>
    <q-dialog v-model="showModal" persistent>
        <q-card style="max-width: 500px;" class="full-width">
            <q-card-section>
                <div class="text-h6 row items-center">
                    <q-icon name="mdi-lock-open-variant-outline" size="sm" class="q-mr-xs" />{{ activationType }} Activation
                    <q-space />
                    <q-btn v-if="!isAutoActivation" icon="mdi-close" flat round dense v-close-popup />
                </div>
            </q-card-section>
            <q-card-section v-if="!isAutoActivation" class="q-pt-none">
                <q-item v-if="licenseData" class="bg-grey-3 rounded-borders">
                    <q-item-section top>
                        <q-item-label overline>{{ licenseData.licenseKeyID }}</q-item-label>
                        <q-item-label caption>
                            {{ licenseData.productDescription }}
                        </q-item-label>
                        <q-item-label caption v-if="licenseData.oemName">
                            OEM: {{ licenseData.oemName }}
                        </q-item-label>
                        <q-item-label caption>
                            Expiry: {{ licenseData.expiry | date }}
                        </q-item-label>
                    </q-item-section>

                    <q-item-section side top>
                        <q-badge transparent v-if="getLicenseStatus(licenseData.expiry)" color="positive">
                            Active
                        </q-badge>
                        <q-badge transparent v-else color="negative">
                            Expired
                        </q-badge>
                    </q-item-section>
                </q-item>
                <q-item v-if="getLicensePending" class="bg-grey-3 rounded-borders q-pa-md">
                    <q-inner-loading :showing="getLicensePending">
                        <div>
                            <q-spinner color="primary"
                                       :thickness="2"
                                       size="3em" />
                        </div>
                    </q-inner-loading>
                </q-item>
            </q-card-section>

            <q-card-section class="q-py-none">
                <p v-if="activationType == 'Secondary' && !isAutoActivation" class="text-caption">
                    <span class="text-weight-medium">Disclaimer:</span><br />
                    This software license is for a single user only, and cannot be shared. Secondary activations are available to you at no charge so that you can use the software on two different computers (i.e work computer and home or laptop computer). If the second activation is intended for a different user then you cannot use the 'Secondary Activation' available here, and instead you will need to purchase a new license for the other user.
                </p>
                <div class="q-col-gutter-md">

                    <q-input ref="computerRef"
                             :readonly="generatedActivation && typeof generatedActivation !== 'undefined'"
                             outlined dense
                             label="Computer Name" stack-label
                             autocomplete="on" name="computerName"
                             v-model="form.computer"
                             :rules="[ val => $v.form.computer.required || 'Computer name is required' ]"
                             hide-bottom-space>
                    </q-input>

                    <validate-install-id ref="validateInstallIdRef"
                                         :readonly="(generatedActivation && typeof generatedActivation !== 'undefined') || installId !== '' || (infoFromSoftware.installId !== '' && isAutoActivation)"
                                         :required="true"
                                         :formInstallId="form.installId"
                                         @updateInstallId="updateInstallId" />

                    <q-list v-if="!generatedActivation">

                        <q-item class="q-pa-none">
                            <q-item-section avatar top>
                                <q-checkbox v-model="form.declarationAccepted" @input="$v.form.$reset()" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label class="text-left">
                                    <q-field ref="declareRef"
                                             :value="form.declarationAccepted"
                                             :rules="[ val => $v.form.declarationAccepted.sameAs || 'Declaration checkbox must be checked to proceed.' ]"
                                             no-error-icon
                                             class="text-caption text-black"
                                             borderless dense
                                             hide-bottom-space>
                                        <span class="text-black cursor-pointer" style="max-width: 415px;" @click="form.declarationAccepted = !form.declarationAccepted">I verify that this activation is for the sole use of me, the licensed user. I understand that the use of the software by anyone other than licensed user will first require the purchase of a new license.</span>
                                    </q-field>
                                </q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-list>
                    <div v-else class="q-mb-md">
                        <q-input outlined
                                 readonly
                                 label="New Activation Code" stack-label
                                 v-model="generatedActivation.activationCode"
                                 input-class="text-positive text-bold"
                                 hide-bottom-space>
                            <template v-slot:append>
                                <q-btn flat color="primary" icon="mdi-content-copy" dense
                                       @click="copyClipboard(generatedActivation.activationCode)">
                                    <q-tooltip>
                                        Copy to clipboard
                                    </q-tooltip>
                                </q-btn>
                            </template>
                        </q-input>
                        <div v-if="!isAutoActivation" class="q-mt-md">
                            For instructions on how to apply your activation code to your software <router-link to="/support/Help/Enter-Activation" target="_blank" style="text-decoration: underline;">click here</router-link>.
                        </div>
                        <div v-else-if="isLegacyActivationRetrieval" class="q-mt-md">
                            For instructions on how to apply your activation code to your software <router-link to="/support/Help/Enter-Activation" target="_blank" style="text-decoration: underline;">click here</router-link> and follow the <b>Manual Input Option</b> steps.
                        </div>
                    </div>
                </div>
            </q-card-section>
            <q-card-section>
                <div v-if="!generatedActivation" class="row q-col-gutter-sm">
                    <div class="col-xs-12 col-sm-auto">
                        <q-btn :label="postLicenseActivationPending || postReplaceLicenseActivationPending ? 'Activating...' : 'Activate'"
                               @click="onSubmit" color="primary" class="full-width" />
                    </div>
                    <div class="col-xs-12 col-sm-auto">
                        <q-btn v-if="!isAutoActivation" label="Cancel" color="primary" flat class="full-width" v-close-popup />
                        <q-btn v-else-if="canGoback" label="Back" color="primary" flat class="full-width" @click="$emit('back')" />
                    </div>
                </div>
                <div v-else>
                    <q-btn v-if="!isAutoActivation" label="Close" color="primary" v-close-popup />
                </div>
            </q-card-section>

            <q-inner-loading :showing="postLicenseActivationPending || postReplaceLicenseActivationPending">
                <div>
                    <q-spinner color="primary"
                               :thickness="2"
                               size="3em" />
                </div>
            </q-inner-loading>
        </q-card>
    </q-dialog>
</template>
<script>
    import { mapState } from 'vuex'
    import { required, sameAs } from 'vuelidate/lib/validators'
    import validateInstallId from '@/components/ValidateInstallationID'
    export default {
        name: 'dialog-activate',
        components: {
            validateInstallId
        },
        props: {
            installId: {
                type: String,
                default: ''
            },
            activation: Object,
            license: Object,
            isAutoActivation: {
                type: Boolean,
                default: false
            },
            isLegacyActivationRetrieval: {
                type: Boolean,
                default: false
            },
            show: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                licenseData: null,
                form: {
                    computer: '',
                    installId: '',
                    declarationAccepted: false,
                },
                generatedActivation: null,
            }
        },
        validations: {
            form: {
                installId: { required },
                computer: { required },
                declarationAccepted: {
                    required,
                    sameAs: sameAs(() => true),
                },
            }
        },
        methods: {
            replaceActivation() {
                const payload = {
                    license: this.licenseData,
                    oldActivation: this.activation,
                    newActivation: this.form,
                }
                this.$store.dispatch('replaceActivation', payload).then(() => {
                    this.generatedActivation = this.postReplaceLicenseActivationData
                    if (this.user) {
                        this.$store.dispatch('getLicensesCustomer', this.user.id).then(() => {
                            this.$emit('generated', { licenseKeyId: this.license.licenseKeyID, activation: this.generatedActivation })
                        })
                    }
                })
            },
            postNewActivation() {
                const payload = {
                    license: this.licenseData,
                    oldActivation: null,
                    newActivation: this.form,
                    type: this.activationType
                }
                this.$store.dispatch('generateActivation', payload).then(() => {
                    this.generatedActivation = this.postLicenseActivationData
                    if (this.user) {
                        this.$store.dispatch('getLicensesCustomer', this.user.id).then(() => {
                            this.$emit('generated', { licenseKeyId: this.license.licenseKeyID, activation: this.generatedActivation })
                        })
                    }
                })
            },
            getLicense() {
                if (this.activation)
                    this.$store.dispatch('getLicense', this.activation.licenseID).then(() => {
                        this.licenseData = this.getLicenseData
                    })
            },
            updateInstallId(installId) {
                if (this.installId == '')
                    this.form.installId = installId
            },
            onSubmit() {
                this.generatedActivation = null
                for (let componentName in this.$refs) {
                    let component = this.$refs[componentName]
                    if (typeof component.validate === 'function') {
                        component.validate()
                    }
                }
                this.$v.form.$touch()
                if (this.$v.form.$pending || this.$v.form.$error || this.$v.form.$invalid) return
                if (this.activation) {
                    this.replaceActivation()
                    return
                }
                if (this.license) {
                    this.postNewActivation()
                    return
                }
            },

            onReset() {
                if (this.installId) this.form.installId = this.installId
                else if (this.infoFromSoftware?.installId) this.form.installId = this.infoFromSoftware?.installId
                else this.form.installId = ''
                this.form.computer = ''
                this.form.declarationAccepted = false
                this.generatedActivation = null
            }
        },
        computed: {
            user() { return this.$store.getters.authUser },
            ...mapState([
                'infoFromSoftware',
                'getLicensePending',
                'getLicenseData',

                'postLicenseActivationPending',
                'postLicenseActivationData',

                'postReplaceLicenseActivationPending',
                'postReplaceLicenseActivationData',

            ]),
            showModal: {
                get() {
                    return this.show
                },
                set(value) {
                    this.$emit('update:show', value)
                    if (value == false) {
                        this.$emit('update:license', null)
                        this.$emit('update:activation', null)
                    }
                }
            },
            activationType() {
                if (this.activation)
                    return "Replace "
                if (this.licenseData) {
                    if (this.licenseData.activations?.filter(a => a.isCurrent).length == 0)
                        return "Primary "
                    else if (this.licenseData.activations?.filter(a => a.isCurrent).length == 1)
                        return "Secondary "
                }
                return ''
            },
            canGoback() {
                return (this.license?.activations?.filter(a => a.isCurrent).length > 0)
            }
        },
        watch: {
            license(newVal) {
                this.licenseData = newVal
                this.onReset()
            },
            activation(newVal) {
                if (newVal && newVal.isCurrent) {
                    if (!this.license) {
                        this.getLicense()
                        if (this.infoFromSoftware?.installId) this.form.installId = this.infoFromSoftware?.installId
                    }
                } else {
                    this.onReset()
                }
            }
        },



    }
</script>
